﻿import { useEffect } from 'react';

import { ComboLicenseForm, type FormErrors } from 'pages/licenses/combo_license/components/ComboLicenseForm';
import { Book, Course, LicenseApi, LicenseFormPayload, Product } from 'apis/LicenseApi';
import { validateLicenseForm } from 'pages/licenses/combo_license/validateLicenseForm';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { DEFAULT_FORM } from 'pages/licenses/combo_license/constants';
import { useSuperState } from 'hooks';
import { useIntl } from 'react-intl';

export type ComboLicenseModalProps = {
    onClose: () => void;
    onSaved: () => void;
    show: boolean;
};

export const ComboLicenseModal = (props: ComboLicenseModalProps) => {

    const { formatMessage } = useIntl();

    const { onClose, onSaved, show } = props;

    const [state, setState, resetState] = useSuperState({
        apiError: '',
        books: [] as Book[],
        courses: [] as Course[],
        errors: {} as FormErrors,
        form: { ...DEFAULT_FORM } as LicenseFormPayload,
        loading: true,
        products: [] as Product[],
        saving: false
    });

    const loadContent = async () => {

        setState({ loading: true });

        let failed = false;
        const onFail = () => {

            failed = true;
            return [];

        };

        const [products, courses, books] = await Promise.all([
            LicenseApi.GetProduct().catch(onFail) as Promise<Product[]>,
            LicenseApi.GetCourse().catch(onFail) as Promise<Course[]>,
            LicenseApi.GetBook().catch(onFail) as Promise<Book[]>
        ]);

        setState({
            apiError: failed ? 'Some content could not be loaded. Please close and try again.' : '',
            books: books ?? [],
            courses: courses ?? [],
            loading: false,
            products: products ?? []
        });

    };

    const handleSave = async () => {

        const errors = validateLicenseForm(state.form);

        if (Object.keys(errors).length > 0) {

            setState({ errors });
            return;

        }

        setState({ apiError: '', saving: true });

        try {

            const result = await LicenseApi.Create(state.form);

            if (result?.Done === false) {

                setState({ apiError: result.Message ?? 'An error occurred.', saving: false });
                return;

            }

            onSaved();
            onClose();

        } catch {

            setState({ apiError: 'An unexpected error occurred.', saving: false });

        }

    };

    useEffect(() => {

        if (!show) return;
        resetState();
        loadContent();

    }, [show]);

    return (
        <GMetrixDialog
            actionButtonClick={handleSave}
            actionButtonDisabled={state.saving}
            actionButtonText={state.saving ? formatMessage({ id: 'saving.bdfa2829', defaultMessage: 'Saving…' }) : formatMessage({ id: 'create_license.57add689', defaultMessage: 'Create License' })}
            dialogLabel={formatMessage({ id: 'create_license.57add689', defaultMessage: 'Create License' })}
            onCancel={onClose}
            show={show}
            size="xl"
        >
            {state.apiError && (
                <div className="alert alert-danger mb-3" role="alert">
                    {state.apiError}
                </div>
            )}
            {state.loading ? (
                <div className={'d-flex align-items-center justify-content-center p-5'}>
                    <GMetrixLoader />
                </div>
            ) : (
                <ComboLicenseForm
                    books={state.books}
                    courses={state.courses}
                    errors={state.errors}
                    onChange={form => setState({ form })}
                    products={state.products}
                    value={state.form}
                />
            )}
        </GMetrixDialog>
    );

};
